import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle, ChevronRight, Shield, Home, Droplets, AlertTriangle, Building2, Wrench, HardHat, MessageCircle, FileText, Award, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { businessConfig, moldConfig, generateMoldServiceSchema, generateLocalBusinessSchema, generateFAQSchema, generateBreadcrumbSchema } from '../data/businessConfig';
import { getAllMoldSubservices } from '../data/moldSubserviceData';
import SEOHead from '../components/SEOHead';
import MoldLeadForm from '../components/MoldLeadForm';
import MoldStickyCTA from '../components/MoldStickyCTA';
import TextLink from '../components/TextLink';
import { CityLinksGrid } from '../components/InternalLinks';

// Mold-specific problem cards
const PROBLEM_CARDS = [
  { icon: Home, title: "Visible mold on walls or ceilings", id: "visible-mold" },
  { icon: Droplets, title: "Musty odor with no visible source", id: "musty-odor" },
  { icon: Droplets, title: "Mold after a plumbing leak", id: "plumbing-leak" },
  { icon: Home, title: "Attic or roof-leak mold", id: "attic-mold" },
  { icon: Wrench, title: "Mold behind drywall", id: "behind-drywall" },
  { icon: Building2, title: "Mold in a commercial property", id: "commercial" },
  { icon: Droplets, title: "Water damage that hasn't dried", id: "water-damage" },
  { icon: HardHat, title: "Need repairs after mold removal", id: "post-repair" }
];

// Before/After Project Component
const BeforeAfterProject = ({ project, title }) => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-neutral-200">
    <div className="bg-neutral-900 px-6 py-4">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <p className="text-neutral-400 text-sm">{project.location}, TX • {project.propertyType}</p>
    </div>
    <div className="grid md:grid-cols-2">
      <div className="relative">
        <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold z-10">BEFORE</div>
        <img 
          src={project.before} 
          alt={project.altBefore}
          className="w-full h-64 object-cover"
          loading="lazy"
          width="600"
          height="256"
          decoding="async"
        />
      </div>
      <div className="relative">
        <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold z-10">AFTER</div>
        <img 
          src={project.after} 
          alt={project.altAfter}
          className="w-full h-64 object-cover"
          loading="lazy"
          width="600"
          height="256"
          decoding="async"
        />
      </div>
    </div>
  </div>
);

const MoldRemediationPage = () => {
  const scrollToForm = () => {
    const form = document.getElementById('mold-lead-form');
    if (form) {
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Schema markup
  const serviceSchema = generateMoldServiceSchema();
  const businessSchema = generateLocalBusinessSchema();
  const faqSchema = generateFAQSchema(moldConfig.faqs);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Mold Remediation", path: "/services/mold-remediation" }
  ]);

  return (
    <div>
      <SEOHead 
        title={moldConfig.seo.titleLicensed}
        description={moldConfig.seo.description}
        keywords={moldConfig.seo.keywords}
        canonical={moldConfig.seo.canonical}
      />
      
      {/* Schema Markup - Rendered in initial HTML for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(businessSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-neutral-900">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=2000')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900 via-neutral-900/95 to-neutral-900/80" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          {/* Breadcrumb - Crawlable */}
          <nav className="flex items-center gap-2 text-sm text-neutral-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/services" className="hover:text-amber-400">Services</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-amber-400">Mold Remediation</span>
          </nav>

          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            {moldConfig.hero.eyebrow}
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {moldConfig.hero.h1}
            </h1>
            <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
              {moldConfig.hero.intro}
            </p>
            
            {/* Primary CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a href={`tel:${businessConfig.phoneTel}`}>
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-6 text-lg w-full sm:w-auto">
                  <Phone className="mr-2 w-5 h-5" />
                  Call {businessConfig.phone}
                </Button>
              </a>
              <TextLink>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 font-semibold px-8 py-6 text-lg w-full sm:w-auto">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Send Photos of the Problem
                </Button>
              </TextLink>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-amber-500 text-amber-400 hover:bg-amber-500 hover:text-white font-semibold px-8 py-6 text-lg w-full sm:w-auto"
                onClick={scrollToForm}
              >
                {moldConfig.hero.primaryCta}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Trust Strip - Only Verified Claims */}
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {businessConfig.trustSignals.map((signal, i) => (
                <div key={i} className="flex items-center gap-2 text-neutral-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-amber-500" />
                  <span>{signal}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* License Verification Banner */}
      <section className="bg-amber-500 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-center md:text-left">
            <div className="flex items-center gap-3">
              <Award className="w-8 h-8 text-neutral-900" />
              <div>
                <p className="font-bold text-neutral-900">Texas Mold Remediation License #{businessConfig.moldLicense.number}</p>
                <a href={businessConfig.moldLicense.verificationUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-neutral-800 underline hover:text-neutral-900">
                  Verify at TDLR →
                </a>
              </div>
            </div>
            <div className="hidden md:block w-px h-10 bg-amber-600" />
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-neutral-900" />
              <span className="font-medium text-neutral-900">Fully Insured • {businessConfig.yearsInBusiness} Years Experience</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Selector Section */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 text-center mb-8">
            What are you seeing in your property?
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {PROBLEM_CARDS.map((card) => (
              <button
                key={card.id}
                onClick={scrollToForm}
                className="bg-white p-5 rounded-xl border-2 border-neutral-200 hover:border-amber-500 hover:shadow-lg transition-all text-left group"
              >
                <card.icon className="w-8 h-8 text-amber-500 mb-3 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium text-neutral-700">{card.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Immediate Guidance Panel */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <FileText className="w-10 h-10 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-neutral-900 mb-2">Already Have a Mold Remediation Protocol?</h2>
                <p className="text-neutral-700 mb-4">
                  Send us the protocol and photos of the affected area. We can review the remediation scope, discuss reconstruction needs, and prepare the next appropriate step.
                </p>
                <Button onClick={scrollToForm} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <FileText className="w-4 h-4 mr-2" />
                  Upload Your Protocol or Photos
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-16">
              
              {/* Texas Assessment/Remediation Separation - CRITICAL FOR COMPLIANCE */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                  How Mold Remediation Works in Texas
                </h2>
                <div className="prose prose-neutral max-w-none">
                  <p className="text-neutral-700 mb-4">
                    Texas separates mold assessment from mold remediation on regulated projects. A licensed Mold Assessment Consultant evaluates the property and prepares the remediation protocol. The remediation contractor performs the work described in that protocol. The independent assessment professional then performs post-remediation clearance when required.
                  </p>
                  <p className="text-neutral-700 mb-4">
                    Gray Contracting does not perform both regulated assessment and remediation on the same project. If you do not yet have an assessment or protocol, we can explain the appropriate next step without representing the assessor as part of our remediation service.
                  </p>
                  <a 
                    href="https://www.tdlr.texas.gov/mld/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-amber-700 font-medium hover:text-amber-800"
                  >
                    Learn more about Texas mold regulations at TDLR →
                  </a>
                </div>
              </div>

              {/* Signs Section */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  Signs You May Need Professional Mold Remediation
                </h2>
                <p className="text-neutral-600 mb-6">
                  Mold is not always limited to the surface you can see. It may develop behind drywall, above ceilings, below flooring, or around areas affected by an unresolved leak.
                </p>
                <ul className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    "Visible growth or recurring discoloration",
                    "Persistent musty or earthy odor",
                    "Mold discovered after plumbing, roof, or HVAC water damage",
                    "Damp drywall, insulation, or framing",
                    "Growth that returns after surface cleaning",
                    "Mold identified by an independent licensed assessor",
                    "Bubbling, peeling, or stained paint",
                    "Soft drywall or damaged ceiling material"
                  ].map((sign, i) => (
                    <li key={i} className="flex items-start gap-3 bg-neutral-50 p-3 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-neutral-700 text-sm">{sign}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Health Note:</strong> If you have health concerns related to your indoor environment, consult a qualified healthcare professional. Gray Contracting does not provide medical advice or diagnoses.
                  </p>
                </div>
              </div>

              {/* Before/After Project Gallery */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  Recent Mold Remediation Projects in Dallas–Fort Worth
                </h2>
                <p className="text-neutral-600 mb-8">
                  See the transformation from mold-contaminated spaces to clean, restored properties.
                </p>
                <div className="space-y-6">
                  <BeforeAfterProject 
                    project={moldConfig.projectImages.bathroom}
                    title="Bathroom Mold Remediation & Reconstruction"
                  />
                  <BeforeAfterProject 
                    project={moldConfig.projectImages.attic}
                    title="Attic Mold Remediation After Roof Leak"
                  />
                  <BeforeAfterProject 
                    project={moldConfig.projectImages.wall}
                    title="Hidden Mold Behind Drywall Remediation"
                  />
                </div>
              </div>

              {/* Remediation Process */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  Our Mold Remediation Process
                </h2>
                <p className="text-neutral-600 mb-8">
                  Every project is different. The exact scope depends on the location, affected materials, moisture source, and requirements of the protocol.
                </p>
                <div className="space-y-4">
                  {moldConfig.process.map((step) => (
                    <div key={step.step} className="flex gap-4 bg-neutral-50 p-6 rounded-xl">
                      <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">{step.step}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-neutral-900 mb-1">{step.title}</h3>
                        <p className="text-neutral-600 text-sm">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mold Services Grid */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-6">
                  Mold Remediation Services for Dallas–Fort Worth Properties
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {moldConfig.services.map((service, i) => (
                    <div key={i} className="bg-white border border-neutral-200 rounded-xl p-5 hover:shadow-lg hover:border-amber-300 transition-all">
                      <h3 className="font-semibold text-neutral-900 mb-2">{service.title}</h3>
                      <p className="text-sm text-neutral-600">{service.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Homeowner Guides - Internal Links to Content Cluster */}
              <div className="bg-gradient-to-r from-neutral-50 to-neutral-100 rounded-2xl p-8">
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  Mold Remediation Guides for Dallas Homeowners
                </h2>
                <p className="text-neutral-600 mb-6">
                  Learn more about the mold remediation process, costs, Texas regulations, and what to expect:
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Link to="/guides/texas-mold-remediation-process" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-neutral-200 hover:border-amber-500 hover:shadow-lg transition-all group">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 group-hover:text-amber-600 text-sm">How Mold Remediation Works in Texas</h3>
                      <p className="text-xs text-neutral-500">Step-by-step process guide</p>
                    </div>
                  </Link>
                  <Link to="/guides/mold-assessment-vs-remediation-texas" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-neutral-200 hover:border-amber-500 hover:shadow-lg transition-all group">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 group-hover:text-amber-600 text-sm">Assessment vs. Remediation</h3>
                      <p className="text-xs text-neutral-500">Texas requirements explained</p>
                    </div>
                  </Link>
                  <Link to="/guides/mold-remediation-cost-dallas" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-neutral-200 hover:border-amber-500 hover:shadow-lg transition-all group">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 group-hover:text-amber-600 text-sm">Mold Remediation Costs</h3>
                      <p className="text-xs text-neutral-500">Pricing factors in Dallas</p>
                    </div>
                  </Link>
                  <Link to="/guides/insurance-mold-remediation-texas" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-neutral-200 hover:border-amber-500 hover:shadow-lg transition-all group">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 group-hover:text-amber-600 text-sm">Insurance Coverage Guide</h3>
                      <p className="text-xs text-neutral-500">What's typically covered</p>
                    </div>
                  </Link>
                </div>
                <div className="mt-6 text-center">
                  <Link to="/guides" className="inline-flex items-center gap-2 text-amber-600 font-medium hover:text-amber-700">
                    View All Mold Guides <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Why Gray Contracting */}
              <div className="bg-neutral-900 text-white rounded-2xl p-8">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  Why Dallas Property Owners Choose Gray Contracting
                </h2>
                <p className="text-neutral-300 mb-6">
                  Mold projects often leave behind removed drywall, insulation, trim, paint, and other damaged finishes. Gray Contracting combines remediation experience with established contracting capabilities, helping property owners move from remediation into reconstruction without starting over with another company.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: Award, title: "Texas License #1776", desc: "Verified mold remediation company" },
                    { icon: Clock, title: `${businessConfig.yearsInBusiness} Years Experience`, desc: "Established contracting since 1969" },
                    { icon: Shield, title: "Fully Insured", desc: "Protection for your property" },
                    { icon: HardHat, title: "Remediation + Reconstruction", desc: "One team from start to finish" },
                    { icon: Users, title: "Family-Owned", desc: "Local DFW business" },
                    { icon: Building2, title: "Residential & Commercial", desc: "All property types served" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <item.icon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">{item.title}</div>
                        <div className="text-sm text-neutral-400">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specialized Mold Services */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  Specialized Mold Services
                </h2>
                <p className="text-neutral-600 mb-6">
                  Different mold situations require different approaches. Learn more about our specialized services:
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {getAllMoldSubservices().map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/mold-remediation/${service.slug}`}
                      className="flex items-center justify-between p-5 bg-white border-2 border-neutral-200 rounded-xl hover:border-amber-500 hover:shadow-lg transition-all group"
                    >
                      <div>
                        <h3 className="font-semibold text-neutral-900 group-hover:text-amber-600">{service.title}</h3>
                        <p className="text-sm text-neutral-500 mt-1">{service.metaDescription.substring(0, 80)}...</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-neutral-400 group-hover:text-amber-500 flex-shrink-0 ml-4" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Service Areas */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  Mold Remediation Across Dallas–Fort Worth
                </h2>
                <p className="text-neutral-600 mb-6">
                  Gray Contracting serves communities throughout the Dallas–Fort Worth area:
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {businessConfig.serviceAreas.map((city, i) => (
                    <span key={i} className="px-4 py-2 bg-neutral-100 rounded-full text-sm font-medium text-neutral-700">
                      {city}, TX
                    </span>
                  ))}
                </div>
                <CityLinksGrid serviceId="mold-remediation" shortTitle="Mold Remediation" />
              </div>

              {/* Cost Section */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  How Much Does Mold Remediation Cost in Dallas?
                </h2>
                <p className="text-neutral-600 mb-4">
                  The cost depends on the size and location of the affected area, the remediation protocol, containment requirements, contaminated materials, accessibility, water-source repairs, clearance requirements, and reconstruction. Gray Contracting provides a project-specific estimate after reviewing the available assessment and scope.
                </p>
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-5">
                  <h3 className="font-semibold text-neutral-900 mb-2">About Insurance Coverage</h3>
                  <p className="text-sm text-neutral-600">
                    Some insurance policies may provide coverage when mold results from a sudden covered water event, while gradual leaks, long-term humidity, or deferred maintenance may be excluded. Coverage depends on the policy and cause of loss. Contact your insurance carrier for a coverage determination. Gray Contracting can provide project documentation within our scope of work.
                  </p>
                </div>
              </div>

              {/* FAQs */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-6">
                  Mold Remediation FAQs
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {moldConfig.faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-b border-neutral-200">
                      <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:text-amber-600 py-4">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-neutral-600 pb-4">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            {/* Sidebar - Lead Form */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24" id="mold-lead-form">
                <MoldLeadForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-amber-500 to-amber-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Request a Mold Remediation Estimate
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Tell us what happened, where the affected area is located, and whether you already have an assessment or remediation protocol. Uploading photos or documents will help us understand the next appropriate step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${businessConfig.phoneTel}`}>
              <Button size="lg" className="bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                Call {businessConfig.phone}
              </Button>
            </a>
            <TextLink>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-6 text-lg">
                <MessageCircle className="mr-2 w-5 h-5" />
                Send Photos
              </Button>
            </TextLink>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-6 text-lg"
              onClick={scrollToForm}
            >
              Request an Estimate
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <MoldStickyCTA onEstimateClick={scrollToForm} />
    </div>
  );
};

export default MoldRemediationPage;
